import { aabbOverlap, centerX, type Box } from '@/systems/box';
import type { ThrownState } from '@/data/throws';

export type Team = 'player' | 'enemy';

/** What an attack does on connect. */
export interface HitSpec {
  damage: number;
  /** Horizontal knockback magnitude (px), applied away from the attacker. */
  knockback: number;
  /** Frames the victim is stunned/uncontrollable. */
  hitstun: number;
  /** When true, a grounded target is launched into a juggle arc. */
  launcher?: boolean;
  /** Attack type tag for SFX key resolution (e.g. 'light1', 'heavy'). Set by attacker. */
  sfxTag?: string;
  /** Weapon def ID when this hit was delivered with a held weapon (e.g. 'bat', 'pipe'). */
  weaponId?: string;
  /** True when this hit came from a hero super move (screen-clear). Used by gib system. */
  isSuper?: boolean;
}

/**
 * Anything that can attack or be hit. Implemented by Entity, but kept as an
 * interface so the collision resolver is testable with plain fakes.
 */
export interface Combatant {
  readonly combatId: number;
  readonly team: Team;
  isKO: boolean;
  /** True when the entity is in hitstun and not already being thrown. */
  readonly isStunned: boolean;
  /** True when jumping or being thrown (off the ground). */
  readonly isAirborne: boolean;
  /** True while this entity is in flight as a thrown projectile. */
  readonly isBeingThrown: boolean;
  /**
   * Elite-tier trait: light hits deal damage but do NOT apply hitstun.
   * Only launcher attacks and hits tagged 'heavy' still stagger this combatant.
   * Sourced from EnemyDef.ignoresHitstun; set on the entity by the Phaser adapter.
   */
  ignoresHitstun?: boolean;
  worldHurtbox(): Box;
  receiveHit(spec: HitSpec, fromCenterX: number): void;
  /** Return current thrown state, or null if not in flight. */
  getThrownState(): ThrownState | null;
  /** Launch this entity as a projectile with the given thrown state. */
  beginThrow(state: ThrownState): void;
}

/**
 * Hitbox — a transient damage volume owned by an attacker, active for a set
 * number of frames. Tracks which combatants it has already hit so a single
 * swing connects at most once per target.
 */
export class Hitbox {
  framesLeft: number;

  private readonly alreadyHit = new Set<number>();

  constructor(
    readonly owner: Combatant,
    public box: Box,
    readonly spec: HitSpec,
    activeFrames: number,
    /** Called once per successful new hit — used by owner to track recovery windows. */
    readonly onHit?: () => void,
  ) {
    this.framesLeft = activeFrames;
  }

  get expired(): boolean {
    return this.framesLeft <= 0;
  }

  tick(): void {
    this.framesLeft -= 1;
  }

  /**
   * Attempt to hit a target. Returns true if a *new* hit landed.
   * @param allowFriendlyFire When true, same-team hits are permitted (e.g. P1 hitting P2).
   * @param damageScale       Multiplier applied to spec.damage before delivering the hit.
   */
  tryHit(target: Combatant, allowFriendlyFire = false, damageScale = 1): boolean {
    if (target === this.owner) return false;
    if (!allowFriendlyFire && target.team === this.owner.team) return false;
    if (target.isKO) return false;
    if (this.alreadyHit.has(target.combatId)) return false;
    if (!aabbOverlap(this.box, target.worldHurtbox())) return false;

    this.alreadyHit.add(target.combatId);
    let spec =
      damageScale !== 1
        ? { ...this.spec, damage: Math.round(this.spec.damage * damageScale) }
        : this.spec;
    // Elite-tier ignoresHitstun: light hits deal damage but skip hitstun entirely.
    // Only launcher attacks and 'heavy'-tagged hits still stagger this combatant.
    if (target.ignoresHitstun && !spec.launcher && spec.sfxTag !== 'heavy') {
      spec = { ...spec, hitstun: 0 };
    }
    target.receiveHit(spec, centerX(this.box));
    this.onHit?.();
    return true;
  }
}

/**
 * CombatSystem — owns active hitboxes + the combatant registry, resolves
 * overlaps each frame, and ages out expired hitboxes.
 */
export class CombatSystem {
  private hitboxes: Hitbox[] = [];

  private combatants: Combatant[] = [];

  private hitpauseFramesLeft = 0;

  /**
   * Optional callback fired once per successful hit.
   * HitJuiceManager wires this in StageScene to trigger hitstop/shake/flash/particles.
   * @param spec     The HitSpec of the connecting attack
   * @param hitX     World-X centre of the hitbox
   * @param targetY  World-Y centre of the target body
   * @param attacker The Combatant that owns the hitbox (optional — absent for thrown collisions)
   * @param target   The Combatant that was hit (optional — absent for thrown collisions)
   */
  onHitConnect?: (
    spec: HitSpec,
    hitX: number,
    targetY: number,
    attacker?: Combatant,
    target?: Combatant,
  ) => void;

  /** When true, combatants on the same team can damage each other. Default false. */
  friendlyFireEnabled = false;

  /** Scales damage dealt by player-team hitboxes. Set from difficultyMods. Default 1. */
  playerDamageScale = 1;

  /** True while a parry screen-freeze is in effect. */
  get isInHitpause(): boolean {
    return this.hitpauseFramesLeft > 0;
  }

  /** Start a hitpause of `frames` frames (used by parry). */
  startHitpause(frames: number): void {
    this.hitpauseFramesLeft = Math.max(this.hitpauseFramesLeft, frames);
  }

  /** Advance the hitpause counter by one frame. Call from StageScene.update(). */
  tickHitpause(): void {
    if (this.hitpauseFramesLeft > 0) this.hitpauseFramesLeft -= 1;
  }

  register(c: Combatant): void {
    this.combatants.push(c);
  }

  unregister(c: Combatant): void {
    this.combatants = this.combatants.filter((x) => x !== c);
  }

  /** All registered combatants (read-only) — used by ProjectileManager for collision. */
  getCombatants(): readonly Combatant[] {
    return this.combatants;
  }

  spawn(hitbox: Hitbox): void {
    this.hitboxes.push(hitbox);
  }

  get activeHitboxes(): readonly Hitbox[] {
    return this.hitboxes;
  }

  /**
   * Find the nearest opponent who can be grabbed.
   *
   * @param x            Attacker world X
   * @param attackerTeam Attacker's team (result must be opposite team)
   * @param grabRange    Max horizontal distance (px)
   * @param commandThrow If true, target need not be stunned
   */
  findGrabTarget(
    x: number,
    attackerTeam: Team,
    grabRange: number,
    commandThrow: boolean,
  ): Combatant | null {
    let nearest: Combatant | null = null;
    let bestDist = Infinity;
    for (const c of this.combatants) {
      if (c.team === attackerTeam) continue;
      if (c.isKO) continue;
      if (c.isAirborne) continue;
      if (!commandThrow && !c.isStunned) continue;
      const hb = c.worldHurtbox();
      const cx = hb.x + hb.w / 2;
      const dist = Math.abs(x - cx);
      if (dist <= grabRange && dist < bestDist) {
        bestDist = dist;
        nearest = c;
      }
    }
    return nearest;
  }

  /** Resolve all active hitboxes against all combatants; age + cull. */
  update(): number {
    let hits = 0;
    for (const hb of this.hitboxes) {
      const scale = hb.owner.team === 'player' ? this.playerDamageScale : 1;
      for (const c of this.combatants) {
        if (hb.tryHit(c, this.friendlyFireEnabled, scale)) {
          hits += 1;
          if (this.onHitConnect) {
            const cb = c.worldHurtbox();
            this.onHitConnect(hb.spec, centerX(hb.box), cb.y + cb.h / 2, hb.owner, c);
          }
        }
      }
      hb.tick();
    }
    this.hitboxes = this.hitboxes.filter((hb) => !hb.expired);
    hits += this.processThrownCollisions();
    return hits;
  }

  /**
   * Check thrown entities against bystanders on the opposing team.
   * Only resolves near ground (elevation ≤ 30 px) to avoid mid-air phantom hits.
   */
  private processThrownCollisions(): number {
    let hits = 0;
    for (const thrown of this.combatants) {
      const state = thrown.getThrownState();
      if (!state) continue;
      if (state.elevation > 30) continue;
      const thrownHb = thrown.worldHurtbox();
      for (const other of this.combatants) {
        if (other === thrown) continue;
        if (other.isKO) continue;
        // Thrown entity was launched by throwerTeam → damages the opposing team
        // (i.e. thrown enemy damages other enemies, same logical team as thrown entity)
        if (other.team === state.throwerTeam) continue;
        if (state.alreadyHit.has(other.combatId)) continue;
        if (!aabbOverlap(thrownHb, other.worldHurtbox())) continue;
        state.alreadyHit.add(other.combatId);
        const flySpec: HitSpec = { damage: state.flyDamage, knockback: 20, hitstun: 18 };
        other.receiveHit(flySpec, centerX(thrownHb));
        hits += 1;
        if (this.onHitConnect) {
          const cb = other.worldHurtbox();
          this.onHitConnect(flySpec, centerX(thrownHb), cb.y + cb.h / 2);
        }
      }
    }
    return hits;
  }

  /**
   * Hit every opponent within `radius` pixels of `attacker` centre.
   * Used by the SPECIAL+UP super move (screen-clear). Returns hit count.
   */
  resolveScreenClear(attacker: Combatant, radius: number, spec: HitSpec): number {
    const attackerCx = centerX(attacker.worldHurtbox());
    const scale = attacker.team === 'player' ? this.playerDamageScale : 1;
    const scaledSpec = scale !== 1 ? { ...spec, damage: Math.round(spec.damage * scale) } : spec;
    let hits = 0;
    for (const t of this.combatants) {
      if (t === attacker) continue;
      if (t.team === attacker.team) continue;
      if (t.isKO) continue;
      if (Math.abs(attackerCx - centerX(t.worldHurtbox())) <= radius) {
        t.receiveHit(scaledSpec, attackerCx);
        hits += 1;
      }
    }
    return hits;
  }

  clear(): void {
    this.hitboxes = [];
    this.combatants = [];
  }
}
